import { JSX } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  LinearProgress,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ModelTrainingIcon from '@mui/icons-material/ModelTraining';
import PsychologyIcon from '@mui/icons-material/Psychology';
import { StyledMarkdown } from '../StyledMarkdown';
import { SkillMatch } from './jobMatchTypes';

interface RequirementAccordionProps {
  match: SkillMatch;
  index: number;
  expanded: string | false;
  matchStatus: string;
  dividerColor: string;
  getMatchColor: (score: number) => string;
  getStatusIcon: (status: string, score: number) => JSX.Element;
  onAccordionChange: (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => void;
  onRegenerate: (index: number, skill: string, force: boolean) => Promise<void>;
  onShowReasoning: (index: number) => void;
}

function statusChip(
  match: SkillMatch,
  getMatchColor: (score: number) => string,
  errorColor: string
): JSX.Element {
  if (match.status === 'complete') {
    return (
      <Chip
        label={`${match.matchScore}% Match`}
        size="small"
        sx={{
          bgcolor: getMatchColor(match.matchScore),
          color: 'white',
          minWidth: 90,
          flexShrink: 0,
          pointerEvents: 'none',
          mr: 1,
        }}
      />
    );
  }
  if (match.status === 'waiting') {
    return (
      <Chip
        label="Waiting..."
        size="small"
        sx={{
          bgcolor: 'rgb(189, 173, 85)',
          color: 'white',
          minWidth: 90,
          flexShrink: 0,
          pointerEvents: 'none',
          mr: 1,
        }}
      />
    );
  }
  if (match.status === 'pending') {
    return (
      <Chip
        label="Analyzing..."
        size="small"
        sx={{
          bgcolor: 'grey.400',
          color: 'white',
          minWidth: 90,
          flexShrink: 0,
          pointerEvents: 'none',
          mr: 1,
        }}
      />
    );
  }
  return (
    <Chip
      label="Error"
      size="small"
      sx={{
        bgcolor: errorColor,
        color: 'white',
        minWidth: 90,
        flexShrink: 0,
        pointerEvents: 'none',
        mr: 1,
      }}
    />
  );
}

function JobRequirementAccordion(props: RequirementAccordionProps): JSX.Element {
  const { match, index, expanded, matchStatus, dividerColor, getMatchColor, getStatusIcon } = props;
  return (
    <Accordion
      expanded={expanded === `panel${index}`}
      onChange={props.onAccordionChange(`panel${index}`)}
      sx={{
        mb: 2,
        border: '1px solid',
        borderColor: match.status === 'complete' ? getMatchColor(match.matchScore) : dividerColor,
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`panel${index}bh-content`}
        id={`panel${index}bh-header`}
        sx={{
          bgcolor: match.status === 'complete' ? `${getMatchColor(match.matchScore)}22` : 'inherit',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            minWidth: 0,
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1 }}>
            {getStatusIcon(match.status, match.matchScore)}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0,
                p: 0,
                m: 0,
                minWidth: 0,
                flex: 1,
              }}
            >
              <Typography
                sx={{
                  ml: 1,
                  mb: 0,
                  fontWeight: 'medium',
                  marginBottom: '0px !important',
                  wordBreak: 'break-word',
                  textAlign: 'left',
                }}
              >
                {match.skill}
              </Typography>
              <Typography
                variant="caption"
                sx={{ ml: 1, fontWeight: 'light', wordBreak: 'break-word', textAlign: 'left' }}
              >
                {match.domain}
              </Typography>
            </Box>
          </Box>
          {statusChip(match, getMatchColor, dividerColor)}
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <RequirementDetails
          match={match}
          index={index}
          matchStatus={matchStatus}
          dividerColor={dividerColor}
          onRegenerate={props.onRegenerate}
          onShowReasoning={props.onShowReasoning}
        />
      </AccordionDetails>
    </Accordion>
  );
}

function RequirementDetails({
  match,
  index,
  matchStatus,
  dividerColor,
  onRegenerate,
  onShowReasoning,
}: {
  match: SkillMatch;
  index: number;
  matchStatus: string;
  dividerColor: string;
  onRegenerate: (index: number, skill: string, force: boolean) => Promise<void>;
  onShowReasoning: (index: number) => void;
}): JSX.Element {
  if (match.status === 'pending') {
    return (
      <Box sx={{ width: '100%', p: 2 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2 }}>
          Analyzing candidate&apos;s match for this requirement...
        </Typography>
        {matchStatus && (
          <Box sx={{ mt: 1, '& pre': { border: 0 } }}>
            <StyledMarkdown content={matchStatus} streaming={true} />
          </Box>
        )}
      </Box>
    );
  }
  if (match.status === 'error') {
    return (
      <Typography color="error">
        {match.assessment || 'An error occurred while analyzing this requirement.'}
      </Typography>
    );
  }
  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Assessment
      </Typography>
      <Typography paragraph sx={{ mb: 3 }}>
        {match.assessment}
      </Typography>
      <Typography variant="h6" gutterBottom>
        Supporting Evidence
      </Typography>
      {match.evidenceDetails?.length ? (
        match.evidenceDetails.map((evidence, evidenceIndex) => (
          <Card
            key={evidenceIndex}
            variant="outlined"
            sx={{ mb: 2, borderLeft: '4px solid', borderColor: dividerColor }}
          >
            <CardContent>
              <Typography variant="body1" component="div" sx={{ mb: 1, fontStyle: 'italic' }}>
                &quot;{evidence.quote}&quot;
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  flexDirection: 'column',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Relevance: {evidence.context}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Source: {evidence.source}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        ))
      ) : (
        <Typography color="text.secondary">
          No specific evidence found in candidate&apos;s profile.
        </Typography>
      )}
      <Typography variant="h6" gutterBottom>
        Skill description
      </Typography>
      <Typography paragraph>{match.description}</Typography>
      <Tooltip title="Regenerate Assessment">
        <IconButton
          size="small"
          onClick={(e): void => {
            e.stopPropagation();
            onRegenerate(index, match.skill, true);
          }}
        >
          <ModelTrainingIcon />
        </IconButton>
      </Tooltip>
      {match.reasoning && (
        <Tooltip title="View Reasoning">
          <IconButton
            size="small"
            onClick={(e): void => {
              e.stopPropagation();
              onShowReasoning(index);
            }}
          >
            <PsychologyIcon />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

export { JobRequirementAccordion };
